// Build-time projection of the Malta districts GeoJSON into ready-to-render
// SVG path strings + label coordinates + margin-shaded fills.
//
// The projection is intentionally simple — Malta is small enough that a
// linear lon → x / lat → y mapping reads correctly, with one trivial
// adjustment: the SVG height is scaled by 1/cos(midLat) so a longitudinal
// degree (which is shorter than a latitudinal degree at Malta's ~36° N)
// renders at the right physical aspect. Without this the map looks visibly
// squashed N–S.
//
// The data source is src/data/malta_districts_2022.geojson — 13 features,
// each carrying the dissolved district outline plus 2022 results and the
// district→locality mapping. Drawing reads only the dissolved geometry so
// the SVG stroke renders the outer district border only, never internal
// locality lines.

import districtsRaw from '../data/malta_districts_2022.geojson?raw';

// ----- GeoJSON types (subset we actually use) -------------------------
export type GeoCoord = [number, number]; // [lon, lat]
export type Ring = GeoCoord[];
export type PolygonCoords = Ring[];

export interface Results2022 {
  pl_votes: number;
  pn_votes: number;
  adpd_votes: number;
  other_votes: number;
  valid_votes: number;
  votes_cast: number;
  registered: number;
  pl_pct: number;
  pn_pct: number;
  adpd_pct: number;
  other_pct: number;
  margin_pp: number;
  winner: 'PL' | 'PN' | string;
  turnout_pct: number;
}

export interface DistrictProperties {
  district: number;
  name: string;
  localities: string[];
  boundary_year?: number;
  results_2022: Results2022;
  /** True for districts whose gazette splits at least one locality at
   *  street level (D2, D6, D7, D10, D12 in 2022). The polygon is drawn
   *  as if the whole locality belongs to the majority-share district. */
  is_approximate?: boolean;
}

export type DistrictGeometry =
  | { type: 'Polygon'; coordinates: PolygonCoords }
  | { type: 'MultiPolygon'; coordinates: PolygonCoords[] };

export interface DistrictFeature {
  type: 'Feature';
  properties: DistrictProperties;
  geometry: DistrictGeometry;
}

export interface DistrictFeatureCollection {
  type: 'FeatureCollection';
  features: DistrictFeature[];
}

// ----- Built output ---------------------------------------------------
export interface ProjectedBbox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BuiltPath {
  id: number;
  name: string;
  path: string;
  color: string;
  labelX: number;
  labelY: number;
  /** Bounding box of the projected geometry, in the same SVG units
   *  as `path` / `viewBox`. Lets consumers fill the district with
   *  band rectangles (clipped to `path`) without re-projecting the
   *  geometry themselves. */
  bbox: ProjectedBbox;
  properties: DistrictProperties;
}

export interface NationalTotal {
  pl_pct: number;
  pn_pct: number;
  others_pct: number;
  turnout_pct: number;
}

export interface BuiltMap {
  paths: BuiltPath[];
  viewBox: string;
  electionDate: string | null;
  national: NationalTotal | null;
}

// ----- Margin → colour -----------------------------------------------
// margin_pp is signed: positive = PL won, negative = PN won.
// At |margin| = 0  → near-white (intensity 0.30)
// At |margin| ≥ 45 → full party colour (intensity 1.0)
const PL_TARGET: [number, number, number] = [207, 27, 43];
const PN_TARGET: [number, number, number] = [0, 61, 165];

export function marginToColor(marginPp: number): string {
  const intensity = 0.3 + (0.7 * Math.min(Math.abs(marginPp), 45)) / 45;
  const target = marginPp >= 0 ? PL_TARGET : PN_TARGET;
  const r = Math.round(255 + (target[0] - 255) * intensity);
  const g = Math.round(255 + (target[1] - 255) * intensity);
  const b = Math.round(255 + (target[2] - 255) * intensity);
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

// ----- Projection helpers --------------------------------------------
function* eachCoord(geom: DistrictGeometry): Generator<GeoCoord> {
  if (geom.type === 'Polygon') {
    for (const ring of geom.coordinates) for (const c of ring) yield c;
  } else {
    for (const poly of geom.coordinates)
      for (const ring of poly) for (const c of ring) yield c;
  }
}

function ringToSubpath(ring: Ring, project: (c: GeoCoord) => [number, number]): string {
  let out = '';
  for (let i = 0; i < ring.length; i++) {
    const [x, y] = project(ring[i]);
    out += (i === 0 ? 'M' : 'L') + x.toFixed(2) + ',' + y.toFixed(2);
  }
  return out + 'Z';
}

function geometryToPath(
  geom: DistrictGeometry,
  project: (c: GeoCoord) => [number, number],
): string {
  if (geom.type === 'Polygon') {
    return geom.coordinates.map((r) => ringToSubpath(r, project)).join(' ');
  }
  return geom.coordinates
    .flatMap((poly) => poly.map((r) => ringToSubpath(r, project)))
    .join(' ');
}

// Bounding box of the projected geometry. Walks every coord (covers
// MultiPolygons correctly) so the box hugs the district outline tight.
function projectedBbox(
  geom: DistrictGeometry,
  project: (c: GeoCoord) => [number, number],
): ProjectedBbox {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of eachCoord(geom)) {
    const [x, y] = project(c);
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// Label point: centroid of the largest outer ring (by vertex count).
// For a MultiPolygon (e.g. district 13: Gozo + Comino) this lands the label
// on the larger island instead of in the channel between them.
function labelPoint(
  geom: DistrictGeometry,
  project: (c: GeoCoord) => [number, number],
): [number, number] {
  let best: Ring | null = null;
  let bestSize = 0;
  if (geom.type === 'Polygon') {
    best = geom.coordinates[0];
  } else {
    for (const poly of geom.coordinates) {
      const outer = poly[0];
      if (outer.length > bestSize) {
        bestSize = outer.length;
        best = outer;
      }
    }
  }
  if (!best || best.length === 0) return [0, 0];
  let sx = 0,
    sy = 0;
  for (const c of best) {
    const [x, y] = project(c);
    sx += x;
    sy += y;
  }
  return [sx / best.length, sy / best.length];
}

// ----- Main builder ---------------------------------------------------
interface FCWithMetadata extends DistrictFeatureCollection {
  metadata?: {
    election_date?: string;
    national_total?: NationalTotal;
  };
}

export function buildMapPaths(fc: FCWithMetadata): BuiltMap {
  let minLon = Infinity,
    maxLon = -Infinity,
    minLat = Infinity,
    maxLat = -Infinity;
  for (const feat of fc.features) {
    for (const [lon, lat] of eachCoord(feat.geometry)) {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  const lonRange = maxLon - minLon;
  const latRange = maxLat - minLat;
  const cosLat = Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180));

  const W = 1200;
  const H = Math.round((W * latRange) / (lonRange * cosLat));
  const project = ([lon, lat]: GeoCoord): [number, number] => {
    const x = ((lon - minLon) / lonRange) * W;
    const y = H - ((lat - minLat) / latRange) * H;
    return [x, y];
  };

  const paths: BuiltPath[] = fc.features.map((feat) => {
    const id = feat.properties.district;
    const name = feat.properties.name;
    const color = marginToColor(feat.properties.results_2022.margin_pp);
    const path = geometryToPath(feat.geometry, project);
    const [labelX, labelY] = labelPoint(feat.geometry, project);
    const bbox = projectedBbox(feat.geometry, project);
    return { id, name, path, color, labelX, labelY, bbox, properties: feat.properties };
  });

  // Render in stable district order so DOM order matches keyboard tab order.
  paths.sort((a, b) => a.id - b.id);

  return {
    paths,
    viewBox: `0 0 ${W} ${H}`,
    electionDate: fc.metadata?.election_date ?? null,
    national: fc.metadata?.national_total ?? null,
  };
}

export const MAP_DATA: BuiltMap = buildMapPaths(
  JSON.parse(districtsRaw) as FCWithMetadata,
);
